import React, { useMemo } from "react";
import {
  Card,
  CardHeader,
  Box,
  Stack,
  Typography,
  Skeleton,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

function StatCard({
  title,
  value,
  loading,
  icon,
  color = "primary",
  showLimit = false,
  limit = null,
  onClick = null,
  clickable = false,
  onLimitChange = null,
  to = null,
}) {
  const theme = useTheme();
  const { t } = useTranslation("common");

  const colorStyle = useMemo(
    () => ({
      color: theme.palette[color].main,
    }),
    [color, theme.palette]
  );

  const displayValue = useMemo(() => {
    if (showLimit && limit !== null) {
      return `${value} / ${limit}`;
    }
    return value;
  }, [value, showLimit, limit]);

  return (
    <Card
      elevation={0}
      component={to ? RouterLink : undefined}
      to={to || undefined}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        textDecoration: "none",
        color: "inherit",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
            : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.1)"
        }`,
        borderRadius: "16px",
        cursor: clickable ? "pointer" : "default",
        transition: clickable ? "all 0.2s ease-in-out" : "none",
        "&:hover": {
          backgroundColor: alpha(theme.palette.action.hover, 0.1),
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
      onClick={clickable && onClick ? onClick : undefined}
    >
      <CardHeader
        title={title}
        sx={{
          ".MuiCardHeader-title": {
            fontSize: "0.95rem",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            letterSpacing: "0.02em",
          },
          pb: 0,
        }}
        action={
          showLimit && limit && onLimitChange ? (
            <Tooltip title={t("common.settings")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onLimitChange();
                }}
                sx={{
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  },
                }}
              >
                <Settings fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null
        }
      />
      <Stack
        spacing={2}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          p: 3,
          pt: 2,
          flex: "1 1 auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1.5,
            borderRadius: "12px",
            background:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.03)",
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              ...colorStyle,
              opacity: 0.9,
              fontSize: "2rem",
            },
          })}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          {loading ? (
            <Skeleton width={80} height={40} />
          ) : (
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                ...colorStyle,
                fontSize: showLimit ? "1.4rem" : "1.8rem",
              }}
            >
              {displayValue}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

export default StatCard;
